/**
 * lesson-plans/index.tsx — Lesson Plan list page.
 *
 * Lives at `/lesson-plans`. Shows all lesson plans the tutor owns
 * (lessonPlanListQuery), with title + description + item count + a
 * "Buat plan baru" CTA. Tutor gate enforced in `beforeLoad`.
 */

import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { StatePanel } from '../../components/ui/StatePanel.js';
import { lessonPlanListQuery } from '../../lib/api/queries.js';
import { freshSessionQuery, type SessionPrincipal } from '../../lib/api/queries.js';

export const Route = createFileRoute('/lesson-plans/')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  component: LessonPlanListPage,
});

function LessonPlanListPage(): ReactNode {
  const list = useQuery(lessonPlanListQuery);
  const plans = list.data?.ok ? list.data.data.plans : [];

  return (
    <main className="page-shell">
      <PageHeader
        eyebrow="Lesson plans"
        title="Rencana kegiatan kelas"
        description="Susun worksheet dan game menjadi satu urutan kegiatan untuk satu kelas."
        action={
                  <Link
                    to="/lesson-plans/new"
                    className="button button-primary"
                    data-testid="link-create-plan"
                  >
                    Buat plan baru
                  </Link>
                }
      />

      <section className="section">
        {list.isLoading ? (
          <p className="form-message">Memuat daftar lesson plan…</p>
        ) : plans.length === 0 ? (
          <StatePanel
            variant="empty"
            title="Belum ada lesson plan"
            description="Mulai dengan membuat plan baru. Anda bisa menambahkan worksheet dan game dari katalog."
            action={
              <Link to="/lesson-plans/new" className="button button-primary">
                Buat plan baru
              </Link>
            }
          />
        ) : (
          <ul className="lesson-plan-list" data-testid="lesson-plan-list">
            {plans.map((p) => (
              <li
                key={p.id}
                className="lesson-plan-card"
                data-testid={`lesson-plan-${p.id}`}
              >
                <div className="lesson-plan-card-body">
                  <Link
                    to="/lesson-plans/$id"
                    params={{ id: p.id }}
                    className="lesson-plan-card-link"
                    aria-label={`Buka lesson plan ${p.title}`}
                  >
                    <h3 className="lesson-plan-title">{p.title}</h3>
                    {p.description ? (
                      <p className="lesson-plan-description">{p.description}</p>
                    ) : null}
                    <p className="lesson-plan-meta">
                      {p.itemCount} item · diperbarui{' '}
                      {new Date(p.updatedAt).toLocaleDateString('id-ID')}
                    </p>
                  </Link>
                  <div className="lesson-plan-card-actions">
                    <Link
                      to="/lesson-plans/$id"
                      params={{ id: p.id }}
                      className="button button-secondary button-sm"
                      data-testid={`open-plan-${p.id}`}
                    >
                      Buka
                    </Link>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}